import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Title1,
  Subtitle2,
  TabList,
  Tab,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Input,
  Button,
  Combobox,
  Option,
  Textarea,
  RadioGroup,
  Radio,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Spinner,
  MessageBar,
  tokens,
} from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { canManageService, useMe } from '../lib/me'
import { formatSyncAge, syncFreshnessColor } from '../lib/syncFreshness'
import { RESOLUTION_INFO } from '../lib/personRows'
import Lozenge from '../components/Lozenge'
import ServiceIconPicker from '../components/ServiceIconPicker'
import OverrideExtraFields from '../components/OverrideExtraFields'
import type {
  GitHubResponse,
  GitHubRepoRow,
  GitHubTeamRow,
  GitHubResolvedAccess,
  GitHubUserRow,
  GitHubUserLinkInfo,
  PersonDirectoryResponse,
  PersonSummary,
} from '../types'

// Matches the /api/service-config / /api/sync-runs convention pages/PeopleForce.tsx already
// established — not a real MODULES ServiceModule, but both endpoints key by plain string, so any
// id works as long as it's used consistently (Dashboard.tsx/Sidebar.tsx read the same key).
const SERVICE_ID = 'github'

type TabValue = 'users' | 'repos' | 'teams' | 'settings'
const VALID_TABS: TabValue[] = ['users', 'repos', 'teams', 'settings']

type Permission = 'pull' | 'triage' | 'push' | 'maintain' | 'admin'
const PERMISSION_RANK: Record<Permission, number> = { pull: 0, triage: 1, push: 2, maintain: 3, admin: 4 }
const PERMISSION_COLOR: Record<Permission, 'subtle' | 'informative' | 'warning' | 'severe' | 'danger'> = {
  pull: 'subtle',
  triage: 'informative',
  push: 'warning',
  maintain: 'severe',
  admin: 'danger',
}

// Same palette PersonLink's ResolutionBadge uses (RESOLUTION_INFO), reused rather than invented —
// 'linked' here is the normal/expected end state (every account should end up linked or
// external/shared), so it gets the same unremarkable color as 'entra' there, not its own alert
// color; 'not linked' is the actionable gap, same severity as 'unresolved' there.
const LINK_STATUS_COLOR: Record<'linked' | 'external' | 'shared' | 'unlinked', 'success' | 'brand' | 'informative' | 'warning' | 'danger'> = {
  linked: RESOLUTION_INFO.entra.color,
  external: RESOLUTION_INFO.external.color,
  shared: RESOLUTION_INFO.shared.color,
  unlinked: RESOLUTION_INFO.unresolved.color,
}
const LINK_STATUS_LABEL: Record<'linked' | 'external' | 'shared' | 'unlinked', string> = {
  linked: 'Linked',
  external: 'External',
  shared: 'Shared',
  unlinked: 'Not linked',
}

function highestPermission(access: GitHubResolvedAccess[]): Permission | null {
  if (access.length === 0) return null
  return access.reduce<Permission>((max, a) => (PERMISSION_RANK[a.permission] > PERMISSION_RANK[max] ? a.permission : max), access[0].permission)
}

/** One line describing who/what a login resolves to, shared by the Login cell (Users tab), the
 * Repos tab's access badges, and the detail dialog — one place to word this consistently. */
function linkSubtext(link: GitHubUserLinkInfo | null | undefined): string | null {
  if (!link) return null
  if (link.status === 'linked') return link.personDisplayName ?? link.personEmail ?? null
  const base = link.status === 'external' ? 'External / bot account' : 'Shared / departmental account'
  return link.responsibleDisplayName ? `${base} · Responsible: ${link.responsibleDisplayName}` : base
}

interface SyncRun {
  startedAt: string
  finishedAt?: string
  recordsProcessed: number
  errorsJson: string
}

function useGitHubData(refreshKey: number) {
  const [data, setData] = useState<GitHubResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  function load() {
    apiFetchJson<GitHubResponse>('/api/github')
      .then(setData)
      .catch(e => setError(String(e)))
  }

  useEffect(load, [refreshKey])

  return { data, error, reload: load }
}

/** Entra directory, narrowed to the shape OverrideExtraFields/person-search Comboboxes need —
 * `/api/persons` reports `department` as `string | null` (Table Storage's Merge-upsert
 * convention), while PersonSummary (shared with UserLinksTab) uses `string | undefined`. */
function usePersons(): PersonSummary[] {
  const [persons, setPersons] = useState<PersonSummary[]>([])

  useEffect(() => {
    apiFetchJson<PersonDirectoryResponse>('/api/persons')
      .then(res =>
        setPersons(
          res.persons.map(p => ({
            email: p.email,
            displayName: p.displayName,
            entraStatus: p.entraStatus,
            department: p.department ?? undefined,
          })),
        ),
      )
      .catch(() => {})
  }, [])

  return persons
}

type LinkMode = 'link' | 'external' | 'shared'

function LinkDialog(props: {
  login: string | null
  mode: LinkMode | null
  persons: PersonSummary[]
  onClose: () => void
  onSaved: () => void
}) {
  const { login, mode: initialMode, persons, onClose, onSaved } = props
  const [mode, setMode] = useState<LinkMode | null>(initialMode)
  const [query, setQuery] = useState('')
  const [selectedPersonEmail, setSelectedPersonEmail] = useState('')
  const [responsibleQuery, setResponsibleQuery] = useState('')
  const [selectedResponsibleEmail, setSelectedResponsibleEmail] = useState('')
  const [department, setDepartment] = useState('')
  const [comment, setComment] = useState('')

  useEffect(() => {
    setMode(initialMode)
    setQuery('')
    setSelectedPersonEmail('')
    setResponsibleQuery('')
    setSelectedResponsibleEmail('')
    setDepartment('')
    setComment('')
  }, [login, initialMode])

  const departments = useMemo(
    () => [...new Set(persons.filter(p => p.entraStatus === 'enabled').map(p => p.department).filter((d): d is string => !!d))].sort(),
    [persons],
  )
  const filteredPersons = query
    ? persons.filter(p => p.displayName.toLowerCase().includes(query.toLowerCase()) || p.email.toLowerCase().includes(query.toLowerCase()))
    : persons

  async function save() {
    if (!login || !mode) return
    await apiFetchJson('/api/github-links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        login,
        status: mode === 'link' ? 'linked' : mode,
        personEmail: mode === 'link' ? selectedPersonEmail : undefined,
        responsibleEmail: mode !== 'link' ? selectedResponsibleEmail || undefined : undefined,
        department: mode !== 'link' ? department || undefined : undefined,
        comment: comment || undefined,
      }),
    })
    onSaved()
  }

  return (
    <Dialog open={login !== null} onOpenChange={(_e, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{mode === 'link' ? 'Link to a known person' : 'Mark as external / shared'} — {login}</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0, fontSize: 13, color: tokens.colorNeutralForeground3 }}>
              {mode === 'link'
                ? "GitHub never reports this account's email, so this is the only way to attribute this login to a real person."
                : 'A CI/bot/service account, a genuine non-employee collaborator, or a shared/departmental account with no single owner.'}
            </p>
            {mode === 'link' && (
              <Combobox
                placeholder="Search person..."
                value={query}
                onChange={e => {
                  setQuery(e.target.value)
                  setSelectedPersonEmail('')
                }}
                onOptionSelect={(_e, d) => {
                  setSelectedPersonEmail(d.optionValue ?? '')
                  setQuery(d.optionText ?? '')
                }}
              >
                {filteredPersons.map(p => (
                  <Option key={p.email} value={p.email} text={p.displayName}>
                    {p.displayName} ({p.email})
                  </Option>
                ))}
              </Combobox>
            )}
            {(mode === 'external' || mode === 'shared') && (
              <RadioGroup layout="horizontal" value={mode} onChange={(_e, d) => setMode(d.value as LinkMode)}>
                <Radio value="external" label="External / bot" />
                <Radio value="shared" label="Shared / departmental" />
              </RadioGroup>
            )}
            {(mode === 'external' || mode === 'shared') && (
              <OverrideExtraFields
                persons={persons}
                departments={departments}
                responsibleQuery={responsibleQuery}
                onResponsibleQueryChange={setResponsibleQuery}
                onResponsibleSelect={setSelectedResponsibleEmail}
                department={department}
                onDepartmentChange={setDepartment}
                comment={comment}
                onCommentChange={setComment}
              />
            )}
            {mode === 'link' && <Textarea placeholder="Comment (optional)" value={comment} onChange={(_e, d) => setComment(d.value)} />}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" disabled={mode === 'link' && !selectedPersonEmail} onClick={save}>
              {mode === 'link' ? 'Link' : 'Confirm'}
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

/** Full breakdown for one GitHub account — every team it belongs to and every repo it can reach
 * (direct or via team, with the source spelled out) — same idea as pages/PersonDetail.tsx's
 * per-service license breakdown, scoped to what a GitHub account actually has instead: access,
 * not licenses. Bounded data (one account's own teams/repos), so a Dialog is enough here — unlike
 * PersonDetail, which grew into a full page once a person's licenses stopped fitting a popup. */
function AccessDetailDialog(props: { user: GitHubUserRow | null; onClose: () => void }) {
  const { user, onClose } = props

  return (
    <Dialog open={user !== null} onOpenChange={(_e, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{user?.login}</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '70vh', overflowY: 'auto' }}>
            {user && (
              <>
                <div>
                  <Lozenge color={user.role === 'admin' ? 'danger' : 'subtle'}>{user.role === 'admin' ? 'Org admin' : 'Org member'}</Lozenge>{' '}
                  <Lozenge color={LINK_STATUS_COLOR[user.link?.status ?? 'unlinked']}>{LINK_STATUS_LABEL[user.link?.status ?? 'unlinked']}</Lozenge>{' '}
                  {isBlocked(user) && <Lozenge color="danger">Blocked</Lozenge>}
                </div>
                {linkSubtext(user.link) && <p style={{ margin: 0 }}>{linkSubtext(user.link)}</p>}
                {user.link?.comment && (
                  <p style={{ margin: 0, fontSize: 13, color: tokens.colorNeutralForeground3 }}>{user.link.comment}</p>
                )}

                <div>
                  <strong>Repository access ({user.access.length})</strong>
                  {user.access.length === 0 ? (
                    <p style={{ color: tokens.colorNeutralForeground3 }}>No repository access.</p>
                  ) : (
                    <ul style={{ marginTop: 4 }}>
                      {user.access.map(a => (
                        <li key={a.repoName} style={{ marginBottom: 4 }}>
                          <strong>{a.repoName}</strong> <Lozenge color="subtle">{a.visibility}</Lozenge>{' '}
                          <Lozenge color={PERMISSION_COLOR[a.permission]}>{a.permission}</Lozenge>
                          <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                            via {a.via.map(v => (v.type === 'direct' ? 'direct grant' : `team "${v.teamName}"`)).join(', ')}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <strong>Teams ({user.teams.length})</strong>
                  {user.teams.length === 0 ? (
                    <p style={{ color: tokens.colorNeutralForeground3 }}>Not a member of any team.</p>
                  ) : (
                    <ul style={{ marginTop: 4 }}>
                      {user.teams.map(t => (
                        <li key={t.slug}>
                          {t.name} {t.role === 'maintainer' && <Lozenge color="informative">Maintainer</Lozenge>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

/** The core "orphaned access" signal, applied to GitHub: a login linked to a person now
 * blocked/disabled in Entra still has live repo access. */
function isBlocked(u: GitHubUserRow): boolean {
  return u.link?.status === 'linked' && u.link.personEntraStatus === 'disabled'
}

function UsersTab(props: {
  data: GitHubResponse
  reload: () => void
  unlinkedOnly: boolean
  onUnlinkedOnlyChange: (v: boolean) => void
  blockedOnly: boolean
  onBlockedOnlyChange: (v: boolean) => void
  canManage: boolean
}) {
  const { data, reload, unlinkedOnly, onUnlinkedOnlyChange, blockedOnly, onBlockedOnlyChange, canManage } = props
  const persons = usePersons()
  const [searchText, setSearchText] = useState('')
  const [editingLogin, setEditingLogin] = useState<string | null>(null)
  const [mode, setMode] = useState<LinkMode | null>(null)
  const [detailUser, setDetailUser] = useState<GitHubUserRow | null>(null)

  const rows = useMemo(() => {
    const text = searchText.trim().toLowerCase()
    return data.users
      .filter(u => !text || u.login.toLowerCase().includes(text) || (u.link?.personDisplayName ?? '').toLowerCase().includes(text))
      .filter(u => !unlinkedOnly || !u.link)
      .filter(u => !blockedOnly || isBlocked(u))
  }, [data.users, searchText, unlinkedOnly, blockedOnly])

  function startEdit(login: string, m: LinkMode) {
    setEditingLogin(login)
    setMode(m)
  }

  async function clearLink(login: string) {
    await apiFetchJson(`/api/github-links?login=${encodeURIComponent(login)}`, { method: 'DELETE' })
    reload()
  }

  const unlinkedCount = data.users.filter(u => !u.link).length
  const blockedCount = data.users.filter(isBlocked).length

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <Input placeholder="Search by login or person..." value={searchText} onChange={(_e, d) => setSearchText(d.value)} style={{ width: 280 }} />
        {blockedCount > 0 && (
          <Button appearance={blockedOnly ? 'primary' : 'secondary'} size="small" onClick={() => onBlockedOnlyChange(!blockedOnly)}>
            Blocked person, still has access ({blockedCount})
          </Button>
        )}
        <Button appearance={unlinkedOnly ? 'primary' : 'secondary'} size="small" onClick={() => onUnlinkedOnlyChange(!unlinkedOnly)}>
          Not linked to a person ({unlinkedCount})
        </Button>
      </div>

      {rows.length === 0 ? (
        <p>No accounts match this filter.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: 220 }}>Login</TableHeaderCell>
                <TableHeaderCell style={{ width: 90 }}>Org role</TableHeaderCell>
                <TableHeaderCell style={{ width: 140 }}>Link status</TableHeaderCell>
                <TableHeaderCell style={{ width: 90 }}>Teams</TableHeaderCell>
                <TableHeaderCell style={{ width: 110 }}>Direct repos</TableHeaderCell>
                <TableHeaderCell style={{ width: 100 }}>Total repos</TableHeaderCell>
                <TableHeaderCell style={{ width: 120 }}>Highest access</TableHeaderCell>
                {canManage && <TableHeaderCell style={{ width: 260 }}>Action</TableHeaderCell>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(u => {
                const top = highestPermission(u.access)
                const statusKey = u.link?.status ?? 'unlinked'
                return (
                  <TableRow key={u.login} onClick={() => setDetailUser(u)} style={{ cursor: 'pointer' }}>
                    <TableCell>
                      <div>{u.login}</div>
                      {linkSubtext(u.link) && (
                        <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{linkSubtext(u.link)}</div>
                      )}
                      {u.link?.comment && <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>{u.link.comment}</div>}
                    </TableCell>
                    <TableCell>{u.role === 'admin' ? <Lozenge color="danger">Admin</Lozenge> : 'Member'}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                        <Lozenge color={LINK_STATUS_COLOR[statusKey]}>{LINK_STATUS_LABEL[statusKey]}</Lozenge>
                        {isBlocked(u) && (
                          <Tooltip
                            content="This Entra account is blocked/disabled but still holds live GitHub access — the core 'orphaned access' problem this tool exists to catch."
                            relationship="description"
                            withArrow
                          >
                            <Lozenge color="danger">Blocked</Lozenge>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{u.teamCount}</TableCell>
                    <TableCell>{u.directRepoCount}</TableCell>
                    <TableCell>{u.access.length}</TableCell>
                    <TableCell>{top && <Lozenge color={PERMISSION_COLOR[top]}>{top}</Lozenge>}</TableCell>
                    {canManage && (
                    <TableCell onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button size="small" onClick={() => startEdit(u.login, 'link')}>
                          Link
                        </Button>
                        <Button size="small" onClick={() => startEdit(u.login, 'external')}>
                          Mark external/shared
                        </Button>
                        {u.link && (
                          <Button size="small" onClick={() => clearLink(u.login)}>
                            Clear
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <LinkDialog
        login={editingLogin}
        mode={mode}
        persons={persons}
        onClose={() => setEditingLogin(null)}
        onSaved={() => {
          setEditingLogin(null)
          reload()
        }}
      />
      <AccessDetailDialog user={detailUser} onClose={() => setDetailUser(null)} />
    </div>
  )
}

function ReposTab(props: { data: GitHubResponse }) {
  const { data } = props
  const [searchText, setSearchText] = useState('')

  const rows = useMemo(() => {
    const text = searchText.trim().toLowerCase()
    return data.repos.filter(r => !text || r.name.toLowerCase().includes(text))
  }, [data.repos, searchText])

  return (
    <div>
      <Input
        placeholder="Search repos..."
        value={searchText}
        onChange={(_e, d) => setSearchText(d.value)}
        style={{ width: 280, marginBottom: 12 }}
      />
      {rows.length === 0 ? (
        <p>No repos match this filter.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: 220 }}>Repo</TableHeaderCell>
                <TableHeaderCell style={{ width: 100 }}>Visibility</TableHeaderCell>
                <TableHeaderCell style={{ width: 90 }}>Archived</TableHeaderCell>
                <TableHeaderCell style={{ width: 130 }}>Direct collaborators</TableHeaderCell>
                <TableHeaderCell style={{ width: 100 }}>Via teams</TableHeaderCell>
                <TableHeaderCell>Access</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r: GitHubRepoRow) => {
                const direct = r.access.filter(a => a.subjectType === 'user')
                const viaTeams = r.access.filter(a => a.subjectType === 'team')
                return (
                  <TableRow key={r.name}>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>
                      <Lozenge color={r.visibility === 'public' ? 'informative' : 'subtle'}>{r.visibility}</Lozenge>
                    </TableCell>
                    <TableCell>{r.archived ? <Lozenge color="warning">Archived</Lozenge> : '—'}</TableCell>
                    <TableCell>{direct.length}</TableCell>
                    <TableCell>{viaTeams.length}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {r.access.map((a, i) => {
                          const label = a.subjectType === 'team' ? a.teamName ?? a.subjectName : a.link?.personDisplayName ?? a.subjectName
                          const tooltip =
                            a.subjectType === 'team'
                              ? `Team: ${a.teamName ?? a.subjectName}`
                              : `Login: ${a.subjectName}${a.link ? ` — ${linkSubtext(a.link)}` : ' — not linked to a person'}`
                          return (
                            <Tooltip key={i} content={tooltip} relationship="description" withArrow>
                              <Lozenge color={PERMISSION_COLOR[a.permission]}>
                                {label} · {a.permission}
                              </Lozenge>
                            </Tooltip>
                          )
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

function TeamsTab(props: { data: GitHubResponse }) {
  const { data } = props
  const [searchText, setSearchText] = useState('')
  const [emptyOnly, setEmptyOnly] = useState(false)

  const rows = useMemo(() => {
    const text = searchText.trim().toLowerCase()
    return data.teams
      .filter(t => !text || t.name.toLowerCase().includes(text) || t.slug.toLowerCase().includes(text))
      .filter(t => !emptyOnly || t.repoCount === 0)
  }, [data.teams, searchText, emptyOnly])

  const emptyCount = data.teams.filter((t: GitHubTeamRow) => t.repoCount === 0).length

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        <Input placeholder="Search teams..." value={searchText} onChange={(_e, d) => setSearchText(d.value)} style={{ width: 280 }} />
        <Button appearance={emptyOnly ? 'primary' : 'secondary'} size="small" onClick={() => setEmptyOnly(v => !v)}>
          No repo access ({emptyCount})
        </Button>
      </div>
      {rows.length === 0 ? (
        <p>No teams match this filter.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: 260 }}>Team</TableHeaderCell>
                <TableHeaderCell style={{ width: 100 }}>Privacy</TableHeaderCell>
                <TableHeaderCell style={{ width: 100 }}>Members</TableHeaderCell>
                <TableHeaderCell style={{ width: 100 }}>Repos</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(t => (
                <TableRow key={t.slug}>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.privacy}</TableCell>
                  <TableCell>{t.memberCount}</TableCell>
                  <TableCell>{t.repoCount === 0 ? <Lozenge color="warning">0</Lozenge> : t.repoCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

function SettingsTab() {
  const [lastRun, setLastRun] = useState<SyncRun | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    apiFetchJson<{ runs: SyncRun[] }>(`/api/sync-runs?serviceId=${SERVICE_ID}`)
      .then(res => setLastRun(res.runs[0] ?? null))
      .catch(e => setError(String(e)))
  }, [])

  const lastSyncedAt = lastRun ? (lastRun.finishedAt ?? lastRun.startedAt) : null
  const errors = lastRun ? (JSON.parse(lastRun.errorsJson) as string[]) : []

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <ServiceIconPicker serviceId={SERVICE_ID} />
      </div>

      <MessageBar intent="info">
        GitHub org access syncs automatically once a day (see `timer/src/functions/githubSyncTimer.ts`).
        Manual sync isn't available here because the GitHub token lives in Key Vault, which the API
        (Static Web App Free tier) has no access to — only the scheduled timer function can reach it.
        This org's plan has no Audit Log API and no SAML SSO — GitHub never reports an email or a
        last-login date for any member here, so login-to-person matching is manual only (see the
        Users tab) and "last login" isn't tracked. See docs/ARCHITECTURE.md.
      </MessageBar>

      <div style={{ marginTop: 16, display: 'flex', gap: 24, alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>Last sync</div>
          <Lozenge color={syncFreshnessColor(lastSyncedAt)}>{formatSyncAge(lastSyncedAt)}</Lozenge>
        </div>
        {errors.length > 0 && (
          <div>
            <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>Last sync errors</div>
            <Lozenge color="danger">{errors.length} errors</Lozenge>
          </div>
        )}
      </div>
      {errors.length > 0 && (
        <MessageBar intent="error" style={{ marginTop: 16 }}>
          {errors.join(' ')}
        </MessageBar>
      )}
      {error && <p>Error: {error}</p>}
    </div>
  )
}

export default function GitHub() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [refreshKey, setRefreshKey] = useState(0)
  const me = useMe()
  const canManage = canManageService(me, 'github')
  const requestedTab = searchParams.get('tab')
  let activeTab: TabValue = VALID_TABS.includes(requestedTab as TabValue) ? (requestedTab as TabValue) : 'users'
  if (activeTab === 'settings' && !canManage) activeTab = 'users'
  const unlinkedOnly = searchParams.get('unlinked') === '1'
  const blockedOnly = searchParams.get('blocked') === '1'
  const { data, error, reload } = useGitHubData(refreshKey)

  function handleTabSelect(value: TabValue) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', value)
      return next
    })
  }

  function setUnlinkedOnly(value: boolean) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value) next.set('unlinked', '1')
      else next.delete('unlinked')
      return next
    })
  }

  function setBlockedOnly(value: boolean) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value) next.set('blocked', '1')
      else next.delete('blocked')
      return next
    })
  }

  function reloadAll() {
    reload()
    setRefreshKey(k => k + 1)
  }

  return (
    <div>
      <Title1>GitHub</Title1>
      <Subtitle2 style={{ display: 'block', marginTop: 4, marginBottom: 16 }}>
        Org members, repos, teams, and who has access to what
      </Subtitle2>

      <TabList selectedValue={activeTab} onTabSelect={(_e, d) => handleTabSelect(d.value as TabValue)} style={{ marginBottom: 16 }}>
        <Tab value="users">Users</Tab>
        <Tab value="repos">Repos</Tab>
        <Tab value="teams">Teams</Tab>
        {canManage && <Tab value="settings">Settings</Tab>}
      </TabList>

      {activeTab === 'settings' ? (
        <SettingsTab />
      ) : error ? (
        <p>Error: {error}</p>
      ) : !data ? (
        <Spinner label="Loading GitHub data..." style={{ marginTop: 24 }} />
      ) : (
        <>
          {activeTab === 'users' && (
            <UsersTab
              data={data}
              reload={reloadAll}
              unlinkedOnly={unlinkedOnly}
              onUnlinkedOnlyChange={setUnlinkedOnly}
              blockedOnly={blockedOnly}
              onBlockedOnlyChange={setBlockedOnly}
              canManage={canManage}
            />
          )}
          {activeTab === 'repos' && <ReposTab data={data} />}
          {activeTab === 'teams' && <TeamsTab data={data} />}
        </>
      )}
    </div>
  )
}
